/**
 * SachivalayamSelector Component
 * Reusable selector with a grouped checkbox list (left) + mandal map (right).
 *
 * Works on the UDA sachivalayam master ds_schvlm_lst_t (scoped by uda_id), not the ULB
 * ward secretariats in adm_svm_lst_t. Ids in and out are ds_schvlm_lst_t.schvlm_id.
 *
 * Sachivalayams and panchayats have no boundaries on record, so the map shows the
 * UDA's MANDAL polygons: a mandal is highlighted when any of its sachivalayams is
 * selected, and clicking a mandal narrows the list to it.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { ListSkeleton } from '@/components/base/Skeleton';
import { createPortal } from 'react-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axiosInstance from '@/services/interceptor';
import { BASE_LAYERS } from './dashboard/MapSettings';
import { udaMapView } from '../lib/udaGeo';
import { WMS_POPUP_HEADER, WMS_POPUP_TITLE, WMS_POPUP_SUBTITLE, WMS_POPUP_CLOSE } from './common/wmsPopupHeader';

const BASE_URL = 'ds-wms';

interface UdaSachivalayam {
  schvlm_id: number;
  schvlm_cd: string;
  schvlm_nm: string;
  pnchyt_id: number | null;
  pnchyt_nm: string | null;
  mndl_id: number | null;
  mndl_nm: string | null;
  has_mandal_boundary: boolean;
}

interface SachivalayamSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  /** ds_schvlm_lst_t.schvlm_id values */
  onSave: (schvlmIds: number[]) => void;
  udaId: number;
  udaName?: string;
  workCode?: string;
  workName?: string;
  /** ds_schvlm_lst_t.schvlm_id values already mapped to the work */
  initialSelectedIds?: number[];
  saving?: boolean;
}

const STYLE_DEFAULT  = { color: '#94a3b8', weight: 1.5, fillColor: '#e2e8f0', fillOpacity: 0.2,  fill: true };
const STYLE_HOVER    = { color: '#f59e0b', weight: 3,   fillColor: '#fef3c7', fillOpacity: 0.5,  fill: true };
const STYLE_SELECTED = { color: '#059669', weight: 3,   fillColor: '#d1fae5', fillOpacity: 0.4,  fill: true };
const STYLE_SEL_HOV  = { color: '#f59e0b', weight: 3,   fillColor: '#a7f3d0', fillOpacity: 0.55, fill: true };
// The mandal the list is currently narrowed to.
const STYLE_FOCUS    = { dashArray: '6 4' };

const NO_MANDAL = -1;

export default function SachivalayamSelector({
  isOpen, onClose, onSave, udaId, udaName, workCode, workName,
  initialSelectedIds = [], saving = false,
}: SachivalayamSelectorProps) {
  const [sachivalayams, setSachivalayams] = useState<UdaSachivalayam[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  // Mandal under the pointer, from either the list or the map.
  const [hoveredMandal, setHoveredMandal] = useState<number | null>(null);
  // Mandal the list is narrowed to (map click or header click). null = all.
  const [mandalFilter, setMandalFilter] = useState<number | null>(null);
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());

  const [splitPercent, setSplitPercent] = useState(42);
  const splitContainerRef = useRef<HTMLDivElement>(null);
  const [baseLayerKey, setBaseLayerKey] = useState('osm');
  const tileLayerRef = useRef<L.TileLayer | null>(null);

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const groupRefsMap = useRef<Map<number, HTMLDivElement>>(new Map());
  const mandalLayersMap = useRef<Map<number, L.Path>>(new Map());

  const handleDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const container = splitContainerRef.current;
    if (!container) return;
    const onMove = (ev: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      const pct = ((ev.clientX - rect.left) / rect.width) * 100;
      setSplitPercent(Math.min(70, Math.max(20, pct)));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      mapRef.current?.invalidateSize();
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setSelectedIds(new Set());
      setSearch('');
      setHoveredMandal(null);
      setMandalFilter(null);
      setCollapsed(new Set());
      groupRefsMap.current.clear();
      mandalLayersMap.current.clear();
    }
  }, [isOpen]);

  // Load the UDA's sachivalayams, then apply the work's current selection
  useEffect(() => {
    if (!isOpen || !udaId) return;
    setLoading(true);
    axiosInstance.get(`${BASE_URL}/sachivalayam-mapping/uda/${udaId}/schvlms`)
      .then(res => {
        const list: UdaSachivalayam[] = (res.data.data || []).map((s: any) => ({
          ...s,
          schvlm_id: Number(s.schvlm_id),
          pnchyt_id: s.pnchyt_id == null ? null : Number(s.pnchyt_id),
          mndl_id: s.mndl_id == null ? null : Number(s.mndl_id),
        }));
        setSachivalayams(list);
        const known = new Set(list.map(s => s.schvlm_id));
        setSelectedIds(new Set(initialSelectedIds.map(Number).filter(id => known.has(id))));
      })
      .catch(() => setSachivalayams([]))
      .finally(() => setLoading(false));
  }, [isOpen, udaId, initialSelectedIds]);

  // Initialize map (once per open) with the UDA's mandal polygons
  useEffect(() => {
    if (!isOpen || !mapContainerRef.current) return;

    const timer = setTimeout(() => {
      if (mapRef.current || !mapContainerRef.current) return;

      const baseLayer = BASE_LAYERS.find(l => l.key === baseLayerKey) || BASE_LAYERS[0];
      const fallback = udaMapView(udaId);
      const map = L.map(mapContainerRef.current, {
        center: fallback.center,
        zoom: fallback.zoom,
        zoomControl: true,
      });
      tileLayerRef.current = L.tileLayer(baseLayer.url, { attribution: baseLayer.attribution, maxZoom: baseLayer.maxZoom || 19 }).addTo(map);
      mapRef.current = map;

      axiosInstance.get(`${BASE_URL}/sachivalayam-mapping/uda/${udaId}/mandal-boundaries`)
        .then(res => {
          const geojson = res.data?.data || res.data;
          if (!(geojson?.features?.length > 0)) return;

          L.geoJSON(geojson, {
            style: () => STYLE_DEFAULT,
            onEachFeature: (feature, featureLayer) => {
              const props = feature.properties || {};
              const mndlId = Number(props.mndl_id);
              mandalLayersMap.current.set(mndlId, featureLayer as L.Path);

              featureLayer.bindTooltip(
                `${props.mndl_nm || ''} mandal - ${props.schvlm_count ?? 0} sachivalayam(s)`,
                { permanent: false, direction: 'center', className: 'text-xs font-medium' }
              );
              // Click narrows the list to this mandal; clicking it again clears that.
              featureLayer.on('click', () => {
                setMandalFilter(prev => (prev === mndlId ? null : mndlId));
                setCollapsed(prev => { const n = new Set(prev); n.delete(mndlId); return n; });
              });
              featureLayer.on('mouseover', () => {
                setHoveredMandal(mndlId);
                groupRefsMap.current.get(mndlId)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
              });
              featureLayer.on('mouseout', () => setHoveredMandal(prev => (prev === mndlId ? null : prev)));
            },
          }).addTo(map);

          map.fitBounds(L.geoJSON(geojson).getBounds(), { padding: [20, 20] });
        })
        .catch(e => console.error('Failed to load mandal boundaries:', e));
    }, 100);

    let resizeObserver: ResizeObserver | undefined;
    if (mapContainerRef.current) {
      resizeObserver = new ResizeObserver(() => mapRef.current?.invalidateSize());
      resizeObserver.observe(mapContainerRef.current);
    }

    return () => {
      clearTimeout(timer);
      resizeObserver?.disconnect();
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        mandalLayersMap.current.clear();
      }
    };
  }, [isOpen, udaId]);

  // Swap base layer
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const layer = BASE_LAYERS.find(l => l.key === baseLayerKey) || BASE_LAYERS[0];
    if (tileLayerRef.current) map.removeLayer(tileLayerRef.current);
    tileLayerRef.current = L.tileLayer(layer.url, { attribution: layer.attribution, maxZoom: layer.maxZoom || 19 }).addTo(map);
    tileLayerRef.current.bringToBack();
  }, [baseLayerKey]);

  // Selected-count per mandal drives the map styling and the group headers.
  const selectedPerMandal = useMemo(() => {
    const m = new Map<number, number>();
    sachivalayams.forEach(s => {
      if (selectedIds.has(s.schvlm_id)) {
        const k = s.mndl_id ?? NO_MANDAL;
        m.set(k, (m.get(k) || 0) + 1);
      }
    });
    return m;
  }, [sachivalayams, selectedIds]);

  // Restyle mandal polygons in place on selection / hover / focus change
  useEffect(() => {
    mandalLayersMap.current.forEach((path, mndlId) => {
      const isSelected = (selectedPerMandal.get(mndlId) || 0) > 0;
      const isHovered = mndlId === hoveredMandal;
      const focus = mndlId === mandalFilter ? STYLE_FOCUS : { dashArray: undefined as any };
      if (isHovered) {
        path.setStyle({ ...(isSelected ? STYLE_SEL_HOV : STYLE_HOVER), ...focus });
        (path as any).bringToFront?.();
        (path as any).openTooltip?.();
      } else {
        path.setStyle({ ...(isSelected ? STYLE_SELECTED : STYLE_DEFAULT), ...focus });
        (path as any).closeTooltip?.();
      }
    });
  }, [selectedPerMandal, hoveredMandal, mandalFilter]);

  const zoomToMandal = (mndlId: number) => {
    const layer = mandalLayersMap.current.get(mndlId) as any;
    if (layer?.getBounds && mapRef.current) {
      mapRef.current.fitBounds(layer.getBounds(), { padding: [30, 30] });
    }
  };

  const toggleOne = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Search across sachivalayam, code, panchayat and mandal, then narrow by mandal.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sachivalayams.filter(s => {
      if (mandalFilter !== null && (s.mndl_id ?? NO_MANDAL) !== mandalFilter) return false;
      if (!q) return true;
      return s.schvlm_nm.toLowerCase().includes(q)
        || String(s.schvlm_cd).toLowerCase().includes(q)
        || (s.pnchyt_nm || '').toLowerCase().includes(q)
        || (s.mndl_nm || '').toLowerCase().includes(q);
    });
  }, [sachivalayams, search, mandalFilter]);

  // Group by mandal; mandals holding a selection come first, then by name.
  const groups = useMemo(() => {
    const byMandal = new Map<number, { mndlId: number; mndlNm: string; hasBoundary: boolean; items: UdaSachivalayam[] }>();
    filtered.forEach(s => {
      const k = s.mndl_id ?? NO_MANDAL;
      if (!byMandal.has(k)) {
        byMandal.set(k, { mndlId: k, mndlNm: s.mndl_nm || 'No mandal', hasBoundary: !!s.has_mandal_boundary, items: [] });
      }
      byMandal.get(k)!.items.push(s);
    });
    const list = Array.from(byMandal.values());
    list.forEach(g => g.items.sort((a, b) =>
      (a.pnchyt_nm || '').localeCompare(b.pnchyt_nm || '') || a.schvlm_nm.localeCompare(b.schvlm_nm)));
    return list.sort((a, b) => {
      const as = (selectedPerMandal.get(a.mndlId) || 0) > 0 ? 0 : 1;
      const bs = (selectedPerMandal.get(b.mndlId) || 0) > 0 ? 0 : 1;
      return as - bs || a.mndlNm.localeCompare(b.mndlNm);
    });
  }, [filtered, selectedPerMandal]);

  const setGroupSelected = (items: UdaSachivalayam[], on: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      items.forEach(s => { if (on) next.add(s.schvlm_id); else next.delete(s.schvlm_id); });
      return next;
    });
  };

  const toggleCollapsed = (mndlId: number) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(mndlId)) next.delete(mndlId); else next.add(mndlId);
      return next;
    });
  };

  const focusedMandalName = mandalFilter === null
    ? null
    : sachivalayams.find(s => (s.mndl_id ?? NO_MANDAL) === mandalFilter)?.mndl_nm || 'selected mandal';

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-5xl mx-4 h-[85vh] flex flex-col">
        {/* Header */}
        <div className={`${WMS_POPUP_HEADER} rounded-t-xl`}>
          <div className="min-w-0">
            <h2 className={`${WMS_POPUP_TITLE} flex items-center gap-2`}>
              <i className="ri-map-pin-2-line text-base text-white/90" />
              Select Sachivalayams{udaName ? ` - ${udaName}` : ''}
            </h2>
            {workCode && (
              <p className={WMS_POPUP_SUBTITLE}>{workCode} — {workName}</p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-white/80">
              Selected: <strong className="text-white">{selectedIds.size}</strong>
            </span>
            <button onClick={onClose} className={WMS_POPUP_CLOSE}>
              <i className="ri-close-line text-lg" />
            </button>
          </div>
        </div>

        {/* Body: List + Map */}
        <div className="flex-1 flex overflow-hidden" ref={splitContainerRef}>
          {/* Left: grouped list */}
          <div style={{ width: `${splitPercent}%` }} className="flex flex-col shrink-0">
            <div className="px-3 py-2 border-b">
              <div className="relative">
                <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input
                  type="text"
                  placeholder="Search sachivalayam, panchayat or mandal..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-8 pr-3 py-1.5 border rounded-lg text-sm w-full focus:outline-none focus:ring-1 focus:ring-indigo-300"
                />
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-1.5 text-xs text-gray-500">
                <span>{filtered.length} sachivalayams in {groups.length} mandal(s)</span>
                {focusedMandalName && (
                  <>
                    <span>|</span>
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700">
                      {focusedMandalName}
                      <button onClick={() => setMandalFilter(null)} title="Show all mandals" className="hover:text-indigo-900">
                        <i className="ri-close-line" />
                      </button>
                    </span>
                  </>
                )}
                {selectedIds.size > 0 && (
                  <>
                    <span>|</span>
                    <span className="text-emerald-600 font-medium">{selectedIds.size} selected</span>
                    <span>|</span>
                    <button onClick={() => setSelectedIds(new Set())} className="text-red-500 hover:text-red-700 underline">
                      Clear all
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-auto px-2 py-1">
              {loading ? (
                <div className="py-2"><ListSkeleton rows={4} avatar={false} /></div>
              ) : groups.length === 0 ? (
                <div className="flex items-center justify-center h-32 text-gray-400 text-sm text-center px-4">
                  {sachivalayams.length === 0 ? 'No sachivalayams on record for this UDA' : 'No sachivalayams found'}
                </div>
              ) : (
                <div className="space-y-1.5">
                  {groups.map(g => {
                    const selCount = g.items.filter(s => selectedIds.has(s.schvlm_id)).length;
                    const allOn = selCount === g.items.length;
                    const isOpenGroup = !collapsed.has(g.mndlId) || !!search;
                    const isHovered = hoveredMandal === g.mndlId;
                    return (
                      <div
                        key={g.mndlId}
                        ref={el => { if (el) groupRefsMap.current.set(g.mndlId, el); }}
                        className={`rounded-lg border ${isHovered ? 'border-amber-300 bg-amber-50/40' : 'border-gray-100'}`}
                      >
                        {/* Mandal header */}
                        <div
                          className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 rounded-t-lg"
                          onMouseEnter={() => setHoveredMandal(g.mndlId)}
                          onMouseLeave={() => setHoveredMandal(null)}
                        >
                          <input
                            type="checkbox"
                            checked={allOn}
                            ref={el => { if (el) el.indeterminate = selCount > 0 && !allOn; }}
                            onChange={() => setGroupSelected(g.items, !allOn)}
                            title={allOn ? 'Unselect all in this mandal' : 'Select all in this mandal'}
                            className="w-3.5 h-3.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                          />
                          <button type="button" onClick={() => toggleCollapsed(g.mndlId)} className="flex-1 min-w-0 flex items-center gap-1 text-left">
                            <i className={`ri-arrow-${isOpenGroup ? 'down' : 'right'}-s-line text-gray-400`} />
                            <span className="text-xs font-semibold text-gray-700 truncate">{g.mndlNm}</span>
                            <span className="text-[10px] text-gray-400 shrink-0">
                              {selCount > 0 ? <span className="text-emerald-600 font-medium">{selCount}/</span> : null}{g.items.length}
                            </span>
                          </button>
                          <button
                            type="button"
                            disabled={!g.hasBoundary}
                            title={g.hasBoundary ? 'Zoom to mandal' : 'No mandal boundary on record'}
                            onClick={() => zoomToMandal(g.mndlId)}
                            className={`shrink-0 w-6 h-6 flex items-center justify-center rounded transition-colors ${
                              g.hasBoundary ? 'hover:bg-indigo-100 text-gray-400 hover:text-indigo-600' : 'text-gray-200 cursor-not-allowed'
                            }`}
                          >
                            <i className="ri-focus-3-line text-sm" />
                          </button>
                        </div>

                        {/* Sachivalayams */}
                        {isOpenGroup && (
                          <div className="py-0.5">
                            {g.items.map(s => {
                              const isSelected = selectedIds.has(s.schvlm_id);
                              return (
                                <label
                                  key={s.schvlm_id}
                                  onMouseEnter={() => setHoveredMandal(g.mndlId)}
                                  onMouseLeave={() => setHoveredMandal(null)}
                                  className={`flex items-center gap-2.5 pl-6 pr-2.5 py-1 cursor-pointer transition-colors ${
                                    isSelected ? 'bg-emerald-50' : 'hover:bg-gray-50'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleOne(s.schvlm_id)}
                                    className="w-3.5 h-3.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <p className={`text-xs font-medium leading-tight ${isSelected ? 'text-emerald-700' : 'text-gray-700'}`}>
                                      {s.schvlm_nm} - <span className="text-gray-400 font-normal">{s.schvlm_cd}</span>
                                    </p>
                                    {s.pnchyt_nm && (
                                      <p className="text-[10px] text-gray-400 leading-tight mt-0.5">Panchayat: {s.pnchyt_nm}</p>
                                    )}
                                  </div>
                                  {isSelected && <i className="ri-checkbox-circle-fill text-emerald-500 text-sm" />}
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Resizable Divider */}
          <div
            onMouseDown={handleDragStart}
            className="w-1.5 cursor-col-resize bg-gray-200 hover:bg-indigo-400 active:bg-indigo-500 transition-colors shrink-0 relative group"
            title="Drag to resize"
          >
            <div className="absolute inset-y-0 -left-1 -right-1" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1 h-8 rounded-full bg-gray-400 group-hover:bg-white transition-colors" />
          </div>

          {/* Right: Map */}
          <div className="flex-1 flex flex-col min-w-0">
            <div className="px-3 py-1.5 bg-gray-50 border-b flex items-center gap-1.5">
              <i className="ri-map-2-line text-indigo-500 text-sm" />
              <span className="text-[11px] font-medium text-gray-600">{udaName || 'UDA'} Mandals</span>
              <span className="text-[10px] text-gray-400 ml-1">— click a mandal to list its sachivalayams</span>
              <div className="ml-auto flex items-center gap-2">
                <select
                  value={baseLayerKey}
                  onChange={e => setBaseLayerKey(e.target.value)}
                  className="text-[10px] border rounded px-1.5 py-0.5 bg-white text-gray-600 focus:outline-none focus:ring-1 focus:ring-indigo-300 cursor-pointer"
                >
                  {BASE_LAYERS.map(l => (
                    <option key={l.key} value={l.key}>{l.label}</option>
                  ))}
                </select>
                <button
                  type="button"
                  title="Zoom to all mandals"
                  onClick={() => {
                    if (mapRef.current && mandalLayersMap.current.size > 0) {
                      const group = L.featureGroup(Array.from(mandalLayersMap.current.values()));
                      mapRef.current.fitBounds(group.getBounds(), { padding: [20, 20] });
                    }
                  }}
                  className="shrink-0 px-2 py-0.5 rounded text-[10px] font-medium text-indigo-600 hover:bg-indigo-100 transition-colors flex items-center gap-1"
                >
                  <i className="ri-fullscreen-line text-xs" /> Zoom to all
                </button>
              </div>
            </div>
            <div ref={mapContainerRef} className="flex-1" />
            <div className="px-3 py-1 border-t bg-gray-50 text-[10px] text-gray-500">
              Sachivalayam boundaries are not on record; the map shows mandals. Green = mandal has a selected sachivalayam.
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t bg-gray-50 rounded-b-xl">
          <span className="text-xs text-gray-500">
            {selectedIds.size} sachivalayam(s) selected
          </span>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-100">
              Cancel
            </button>
            <button
              onClick={() => onSave(Array.from(selectedIds))}
              disabled={saving}
              className="px-4 py-2 text-sm text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50 flex items-center gap-1.5"
            >
              {saving ? (
                <><div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" /> Saving...</>
              ) : (
                <><i className="ri-save-line" /> Save</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
